import {
    ENVIRONMENT_LABEL,
    EnvironmentScope,
    useClientTarget,
    type ClientTarget,
} from '@features/clients';
import { Button, Card, EmptyState, ErrorState, LoadingRows, PageHeader } from '@shared/ui';
import { useEffect, useState } from 'react';

import {
    useBulkJob,
    useBulkJobs,
    useBulkModules,
    useMigrationLocation,
} from '../hooks/use-bulk-migrations';
import { FINAL_REPORT_VIEW, stepFor } from '../model/navigation';

import { AnalyzePanel } from './AnalyzePanel';
import { DryRunView } from './DryRunView';
import { FinalReportView } from './FinalReportView';
import { JobHistory } from './JobHistory';
import { JobProgressCard } from './JobProgressCard';
import { LaunchView } from './LaunchView';
import { ModulePicker } from './ModulePicker';

function environmentName(target: ClientTarget): string {
    return target.slot
        ? `${ENVIRONMENT_LABEL[target.env]} · ${target.slot}`
        : ENVIRONMENT_LABEL[target.env];
}

/** A clock for the "no response" check, ticking every 15 s. */
function useNow(): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(Date.now());
        }, 15_000);
        return () => {
            clearInterval(timer);
        };
    }, []);
    return now;
}

/**
 * Salesforce → V2 migration for many clients at once, one module at a time.
 *
 * pick a module → analyze (a dry-run) → choose among the migratable clients →
 * migrate → follow the runs → read the final report. Where the operator is
 * lives in the URL (`?env=&slot=&module=&report=&view=`), so a reload or a
 * shared link reopens the same job at the same step. Every decision — what is migratable, whether a launch
 * may name a client — is the runner's; this screen shows it and asks.
 */
export function BulkMigrationPage() {
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const location = useMigrationLocation();
    const modules = useBulkModules(target);
    const jobs = useBulkJobs(target, location.moduleId);
    const job = useBulkJob(target, location.moduleId, location.reportId);
    const now = useNow();

    // A refresh opened by its own id (an old link) is shown at the launch it
    // refreshed: the launch view is where its runs are read. The view is kept,
    // so a final-report link naming a refresh still opens the report.
    const refreshOf = job.data?.mode === 'report' ? job.data.fromReportId : null;
    const { openReport } = location;
    useEffect(() => {
        if (refreshOf !== null) openReport(refreshOf, true);
    }, [refreshOf, openReport]);

    const module = modules.data?.find((candidate) => candidate.moduleId === location.moduleId);
    const step = stepFor(location.moduleId, job.data, location.reportId, location.view);
    const environment = environmentName(target);
    // The report link, while its job is still loading, is already the report.
    const reportPage =
        step === 'final-report' ||
        (location.view === FINAL_REPORT_VIEW && location.reportId !== null && job.isPending);

    return (
        <div className="max-w-[1320px] print:max-w-none">
            {/* The app's own controls are not part of a printed report. */}
            <div className="print:hidden">
                {reportPage ? (
                    // The report is its own document with its own masthead: only
                    // the way back sits above it, so the page opens on the report.
                    <div className="mb-5">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                location.openReport(location.reportId);
                            }}
                        >
                            ← Back to migration
                        </Button>
                    </div>
                ) : (
                    <PageHeader
                        eyebrow="Tenants"
                        title="Salesforce migration"
                        description="Analyze what a migration module would move out of Salesforce for each client, then migrate the ones you choose."
                        actions={
                            location.moduleId !== null ? (
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        if (location.reportId !== null) location.openReport(null);
                                        else location.openModule(null);
                                    }}
                                >
                                    {location.reportId !== null ? 'Back to module' : 'All modules'}
                                </Button>
                            ) : undefined
                        }
                    />
                )}

                {(!reportPage || isSlotMissing) && <EnvironmentScope state={targetState} />}
            </div>

            {isSlotMissing ? (
                <Card>
                    <EmptyState
                        title="No slot chosen"
                        description="Pick a slot above to point at one of the ephemeral deployments."
                    />
                </Card>
            ) : step === 'pick-module' ? (
                <ModulePicker query={modules} onPick={location.openModule} />
            ) : modules.isPending ? (
                <LoadingRows label="Loading module…" />
            ) : module === undefined ? (
                <Card>
                    {modules.isError ? (
                        <ErrorState error={modules.error} onRetry={() => void modules.refetch()} />
                    ) : (
                        <EmptyState
                            title="Unknown module"
                            description={`The runner in ${environment} has no module "${location.moduleId ?? ''}".`}
                        />
                    )}
                </Card>
            ) : step === 'analyze' ? (
                <>
                    <h2 className="mb-3 text-[18px] font-semibold text-content">{module.title}</h2>
                    <AnalyzePanel
                        target={target}
                        module={module}
                        onStarted={(started) => {
                            location.openReport(started.reportId);
                        }}
                    />
                    <JobHistory
                        query={jobs}
                        onOpen={(reportId) => {
                            location.openReport(reportId);
                        }}
                        onOpenFinalReport={location.openFinalReport}
                    />
                </>
            ) : job.isPending ? (
                <LoadingRows label="Loading job…" />
            ) : job.isError ? (
                <Card>
                    <ErrorState error={job.error} onRetry={() => void job.refetch()} />
                </Card>
            ) : step === 'final-report' ? (
                <FinalReportView
                    target={target}
                    module={module}
                    environment={environment}
                    launchReportId={job.data.reportId}
                />
            ) : (
                <>
                    <h2 className="mb-3 text-[18px] font-semibold text-content">{module.title}</h2>
                    <JobProgressCard job={job.data} now={now} />
                    {step === 'dry-run' ? (
                        <DryRunView
                            target={target}
                            environment={environment}
                            module={module}
                            job={job.data}
                            onLaunched={(launch) => {
                                location.openReport(launch.reportId);
                            }}
                        />
                    ) : (
                        <LaunchView
                            target={target}
                            module={module}
                            launch={job.data}
                            jobs={jobs.data ?? []}
                            now={now}
                            onOpenFinalReport={() => {
                                location.openFinalReport(job.data.reportId);
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
}
